import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axiosInstance from '../utils/axios-auth';
// Async thunk for fetching current student
export const fetch_current_student = createAsyncThunk('student/fetchCurrentStudent', async (_, { rejectWithValue }) => {
    try {
        const response = await axiosInstance.get('/students/current');
        return response.data.data;
    }
    catch (error) {
        console.error('❌ fetch_current_student: Error:', error);
        // Ensure we only store serializable error data
        let errorMessage = 'Failed to fetch student';
        if (error?.response?.data?.message) {
            errorMessage = error.response.data.message;
        }
        else if (error?.message) {
            errorMessage = error.message;
        }
        else if (typeof error === 'string') {
            errorMessage = error;
        }
        return rejectWithValue(errorMessage);
    }
});
// Async thunk for updating student settings
export const update_student_settings = createAsyncThunk('student/updateSettings', async (settings, { rejectWithValue }) => {
    try {
        // Ensure parent_email is a string, not undefined or null
        const updatedSettings = {
            ...settings,
            parent_email: settings.parent_email ?? '' // Use nullish coalescing to ensure it's always a string
        };
        // Check for specific fields that require admin privileges
        if (updatedSettings.email) {
            // We'll keep email in the payload to debug the 403 error
        }
        // Log the full settings payload for debugging
        // Use a standard endpoint that should be implemented in the server to use RPC functions
        const response = await axiosInstance.put('/students/settings', updatedSettings);
        // The API returns a response with { success: true, data: Student }
        if (!response.data || !response.data.success || !response.data.data) {
            throw new Error('Invalid response format from server or student data missing');
        }
        // Return the response structure directly as expected by the reducer
        return response.data;
    }
    catch (error) {
        console.error('Student settings update failed:', error.response?.data || error.message);
        // Ensure we only store serializable error data
        let errorMessage = 'Failed to update settings';
        if (error?.response?.data?.message) {
            errorMessage = error.response.data.message;
        }
        else if (error?.message) {
            errorMessage = error.message;
        }
        else if (typeof error === 'string') {
            errorMessage = error;
        }
        return rejectWithValue({
            message: errorMessage
        });
    }
});
// Add a new thunk for updating student addresses separately
export const update_student_address = createAsyncThunk('student/updateAddress', async (addressData, { rejectWithValue }) => {
    try {
        const response = await axiosInstance.post('/students/address', addressData);
        return response.data;
    }
    catch (error) {
        console.error('Student address update failed:', error.response?.data || error.message);
        // Ensure we only store serializable error data
        let errorMessage = 'Failed to update address';
        if (error?.response?.data?.message) {
            errorMessage = error.response.data.message;
        }
        else if (error?.message) {
            errorMessage = error.message;
        }
        else if (typeof error === 'string') {
            errorMessage = error;
        }
        return rejectWithValue({
            message: errorMessage
        });
    }
});
// Async thunk for fetching student by ID
export const fetch_student_by_id = createAsyncThunk('student/fetchById', async (id, { rejectWithValue }) => {
    try {
        const response = await axiosInstance.get(`/students/${id}`);
        return response.data.data;
    }
    catch (error) {
        console.error('❌ fetch_student_by_id: Error:', error);
        // Ensure we only store serializable error data
        let errorMessage = 'Failed to fetch student';
        if (error?.response?.data?.message) {
            errorMessage = error.response.data.message;
        }
        else if (error?.message) {
            errorMessage = error.message;
        }
        else if (typeof error === 'string') {
            errorMessage = error;
        }
        return rejectWithValue(errorMessage);
    }
});
// Async thunk for fetching student's classrooms
export const fetch_student_classrooms = createAsyncThunk('student/fetchClassrooms', async (studentId, { rejectWithValue }) => {
    try {
        const response = await axiosInstance.get(`/students/${studentId}/classrooms`);
        // Validate and convert the status to the correct type
        const classrooms = response.data.data.map((classroom) => ({
            classroom_id: classroom.classroom_id,
            status: classroom.status || 'pending'
        }));
        return classrooms;
    }
    catch (error) {
        console.error('❌ fetch_student_classrooms: Error:', error);
        // Ensure we only store serializable error data
        let errorMessage = 'Failed to fetch student classrooms';
        if (error?.response?.data?.message) {
            errorMessage = error.response.data.message;
        }
        else if (error?.message) {
            errorMessage = error.message;
        }
        else if (typeof error === 'string') {
            errorMessage = error;
        }
        return rejectWithValue(errorMessage);
    }
});
// Initial state
const initialState = {
    current_student: null,
    status: 'idle',
    error: null
};
// Create the slice
const student_slice = createSlice({
    name: 'student',
    initialState,
    reducers: {
        clear_student: (state) => {
            state.current_student = null;
            state.error = null;
        },
        update_classrooms: (state, action) => {
            if (state.current_student) {
                state.current_student.classrooms = action.payload;
            }
        }
    },
    extraReducers: (builder) => {
        builder
            // Fetch current student cases
            .addCase(fetch_current_student.pending, (state) => {
            state.status = 'loading';
        })
            .addCase(fetch_current_student.fulfilled, (state, action) => {
            state.status = 'succeeded';
            state.current_student = action.payload;
            state.error = null;
        })
            .addCase(fetch_current_student.rejected, (state, action) => {
            state.status = 'failed';
            state.error = action.payload || 'An error occurred';
        })
            // Update student settings cases
            .addCase(update_student_settings.pending, (state) => {
            state.status = 'loading';
        })
            .addCase(update_student_settings.fulfilled, (state, action) => {
            state.status = 'succeeded';
            state.current_student = action.payload.data;
            state.error = null;
        })
            .addCase(update_student_settings.rejected, (state, action) => {
            state.status = 'failed';
            state.error = action.payload?.message || 'Failed to update settings';
        })
            // Update student address cases
            .addCase(update_student_address.pending, (state) => {
            state.status = 'loading';
        })
            .addCase(update_student_address.fulfilled, (state, action) => {
            state.status = 'succeeded';
            if (state.current_student && action.payload.data) {
                state.current_student.address_id = action.payload.data.address_id;
                state.current_student.address = action.payload.data.address;
                // Profile completion is now handled by consent management
                // No need to set profile_completed as it's been removed from schema
            }
            state.error = null;
        })
            .addCase(update_student_address.rejected, (state, action) => {
            state.status = 'failed';
            state.error = action.payload?.message || 'Failed to update address';
        })
            // Fetch student by ID cases
            .addCase(fetch_student_by_id.pending, (state) => {
            state.status = 'loading';
        })
            .addCase(fetch_student_by_id.fulfilled, (state, action) => {
            state.status = 'succeeded';
            state.current_student = action.payload;
            state.error = null;
        })
            .addCase(fetch_student_by_id.rejected, (state, action) => {
            state.status = 'failed';
            state.error = action.payload || 'An error occurred';
        })
            // Fetch student classrooms
            .addCase(fetch_student_classrooms.fulfilled, (state, action) => {
            if (state.current_student) {
                state.current_student.classrooms = action.payload;
            }
        });
    },
});
export const { clear_student, update_classrooms } = student_slice.actions;
export default student_slice.reducer;
